import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api.js';
import { StatusPill, type PillVariant } from '../../components/ui/StatusPill.js';
import '../../components/ui/ui.css';
import './auditlog.css';

interface AuditEntry {
  id: number | string;
  ts: string;
  actorUserId?: string | null;
  actorUsername?: string | null;
  actorIp?: string | null;
  action: string;
  targetType?: string | null;
  targetId?: string | null;
  outcome: string;
  metadata?: unknown;
  requestId?: string | null;
}

const PAGE_SIZE = 50;

function normalizeEntries(data: unknown): AuditEntry[] {
  if (Array.isArray(data)) return data as AuditEntry[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    for (const key of ['entries', 'items', 'rows', 'data']) {
      if (Array.isArray(obj[key])) return obj[key] as AuditEntry[];
    }
  }
  return [];
}

function fmtTs(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function outcomeVariant(outcome: string): PillVariant {
  if (outcome === 'success') return 'ok';
  if (outcome === 'failure' || outcome === 'error' || outcome === 'denied') return 'err';
  return 'neutral';
}

function actorLabel(e: AuditEntry): string {
  if (e.actorUsername) return e.actorUsername;
  if (e.actorUserId) return e.actorUserId.slice(0, 8);
  return 'system';
}

function targetLabel(e: AuditEntry): string {
  if (!e.targetType && !e.targetId) return '—';
  const id = e.targetId ? (e.targetId.length > 12 ? `${e.targetId.slice(0, 12)}…` : e.targetId) : '';
  return [e.targetType, id].filter(Boolean).join(' ');
}

function metadataPreview(metadata: unknown): string {
  if (metadata === null || metadata === undefined) return '—';
  const json = JSON.stringify(metadata);
  return json.length > 60 ? `${json.slice(0, 60)}…` : json;
}

export function AuditLogPage() {
  const [offset, setOffset] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [actionInput, setActionInput] = useState('');
  const [expanded, setExpanded] = useState<string | number | null>(null);

  const { data, isLoading, error } = useQuery<unknown, Error>({
    queryKey: ['audit-log', { limit: PAGE_SIZE, offset, action: actionFilter }],
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (actionFilter) params.set('action', actionFilter);
      return apiFetch<unknown>(`/audit-log?${params.toString()}`);
    },
    // the backend route may not exist yet — don't hammer it with retries
    retry: (failureCount, err) =>
      !(err instanceof ApiError && err.status === 404) && failureCount < 1,
  });

  const notAvailable = error instanceof ApiError && error.status === 404;
  const entries = normalizeEntries(data);

  const applyFilter = (e: FormEvent) => {
    e.preventDefault();
    setOffset(0);
    setExpanded(null);
    setActionFilter(actionInput.trim());
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Audit log</h1>
          <p className="page-sub">Every state-changing action, who did it, and how it went.</p>
        </div>
        <form className="audit-filter" onSubmit={applyFilter}>
          <input
            className="mono"
            placeholder="filter by action, e.g. auth.login"
            value={actionInput}
            onChange={(e) => setActionInput(e.target.value)}
            aria-label="Filter by action"
          />
          <button type="submit" className="btn btn-ghost btn-sm">
            Filter
          </button>
        </form>
      </div>

      {notAvailable ? (
        <div className="card audit-unavailable">
          <p className="mono">audit API not yet available</p>
          <p className="page-sub">
            The <span className="mono">/audit-log</span> endpoint has not been deployed on this
            backend yet.
          </p>
        </div>
      ) : (
        <>
          <div className="card ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Outcome</th>
                  <th>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="ui-table-empty">
                      {isLoading
                        ? 'Loading…'
                        : error
                          ? `Could not load audit log: ${error.message}`
                          : 'No audit entries match.'}
                    </td>
                  </tr>
                ) : (
                  entries.map((entry) => {
                    const isOpen = expanded === entry.id;
                    const hasMeta = entry.metadata !== null && entry.metadata !== undefined;
                    return [
                      <tr
                        key={entry.id}
                        className={hasMeta ? 'ui-row-click' : undefined}
                        onClick={
                          hasMeta ? () => setExpanded(isOpen ? null : entry.id) : undefined
                        }
                      >
                        <td className="ui-cell-mute audit-ts">{fmtTs(entry.ts)}</td>
                        <td className="mono audit-actor">{actorLabel(entry)}</td>
                        <td>
                          <span className="mono audit-action">{entry.action}</span>
                        </td>
                        <td className="mono ui-cell-mute">{targetLabel(entry)}</td>
                        <td>
                          <StatusPill variant={outcomeVariant(entry.outcome)}>
                            {entry.outcome}
                          </StatusPill>
                        </td>
                        <td className="mono audit-meta-preview">
                          {hasMeta ? (
                            <span title={isOpen ? 'collapse' : 'expand'}>
                              <span className="audit-caret">{isOpen ? '▾' : '▸'}</span>{' '}
                              {metadataPreview(entry.metadata)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>,
                      isOpen && hasMeta ? (
                        <tr key={`${entry.id}-meta`} className="audit-meta-row">
                          <td colSpan={6}>
                            <pre className="mono">{JSON.stringify(entry.metadata, null, 2)}</pre>
                          </td>
                        </tr>
                      ) : null,
                    ];
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="audit-pager">
            <button
              className="btn btn-ghost btn-sm"
              disabled={offset === 0 || isLoading}
              onClick={() => {
                setExpanded(null);
                setOffset(Math.max(0, offset - PAGE_SIZE));
              }}
            >
              ← Newer
            </button>
            <span className="mono audit-pager-pos">
              {offset + 1}–{offset + Math.max(entries.length, 1)}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              disabled={entries.length < PAGE_SIZE || isLoading}
              onClick={() => {
                setExpanded(null);
                setOffset(offset + PAGE_SIZE);
              }}
            >
              Older →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
